import React from 'react';
import * as R from 'ramda';
import * as Yup from 'yup';
import { withFormik } from 'formik';
import { connect } from 'react-redux';
import { pure, compose, withProps, withHandlers } from 'react-recompose';
// components
import { FormFooter } from '../../../components/form-footer';
import { LocalLoader } from '../../../components/local-loader';
import { openModal, closeModal } from '../../../components/modal/actions';
// helpers/constants
import * as G from '../../../helpers';
import * as GC from '../../../constants';
import {
  FLAT_PERCENT_RATE_UNIT_OPTIONS,
  getCustomerContractUpliftApplyToEnumOptions,
} from '../../../helpers/options';
// forms
import { Fieldset2 } from '../../../forms';
// hocs
import { withAsyncConfigs } from '../../../hocs';
//////////////////////////////////////////////////

const inputWrapperStyles = {
  mb: 25,
  width: 270,
};

const getApplyToOptions = (values) => {
  const { rateUnit } = values;

  if (R.equals(rateUnit, GC.CHARGE_RATE_UNIT_TYPE_PERCENT)) {
    return getCustomerContractUpliftApplyToEnumOptions();
  }

  return R.tail(getCustomerContractUpliftApplyToEnumOptions());
};

const getFieldSettings = ({ values }) => ([
  {
    type: 'toggle',
    inputWrapperStyles,
    label: ['titles:sync-charges'],
    fieldName: GC.FIELD_SYNC_CHARGES,
  },
  {
    type: 'number',
    isRequired: true,
    inputWrapperStyles,
    label: ['titles:uplift-value'],
    fieldName: GC.FIELD_UPLIFT_VALUE,
  },
  {
    type: 'select',
    isRequired: true,
    inputWrapperStyles,
    label: ['titles:uplift-unit'],
    fieldName: GC.FIELD_RATE_UNIT,
    options: G.prependEmptyLabelValueOption(FLAT_PERCENT_RATE_UNIT_OPTIONS),
  },
  {
    type: 'select',
    isRequired: true,
    inputWrapperStyles,
    label: ['titles:apply-to'],
    fieldName: GC.FIELD_APPLY_TO,
    options: getApplyToOptions(values),
  },
]);

const defaultFields = {
  [GC.FIELD_APPLY_TO]: null,
  [GC.FIELD_RATE_UNIT]: null,
  [GC.FIELD_UPLIFT_VALUE]: 0,
  [GC.FIELD_SYNC_CHARGES]: false,
};

const validationSchema = Yup.object().shape({
  [GC.FIELD_APPLY_TO]: Yup.string().nullable(true).required(G.getRequiredLocaleTxt()),
  [GC.FIELD_RATE_UNIT]: Yup.string().nullable(true).required(G.getRequiredLocaleTxt()),
  [GC.FIELD_SYNC_CHARGES]: Yup.boolean().nullable(true).required(G.getRequiredLocaleTxt),
  [GC.FIELD_UPLIFT_VALUE]: Yup.number()
    .nullable(true)
    .required(G.getRequiredLocaleTxt())
    .positive(G.getShouldBePositiveLocaleTxt())
    .max(9999999, G.getShouldBeLessOrEqualLocaleTxt(999999))
    .typeError('Should be a ${type}'), // eslint-disable-line
});

const ApplyOrderRateUpliftForm = (props) => (
  <LocalLoader minWidth={270} localLoaderOpen={G.isNilOrEmpty(props.asyncConfigs)}>
    <form onSubmit={props.handleSubmit}>
      <Fieldset2
        {...G.getFormikProps(props)}
        fields={getFieldSettings(props)}
        fieldsWrapperStyles={{ pt: 10, flexDirection: 'column' }}
      />
      <FormFooter />
    </form>
  </LocalLoader>
);

const enhance = compose(
  withProps({
    configsNamesArray: [
      GC.CLO_RATE_SYNC_UPLIFT_UNIT,
      GC.CLO_RATE_SYNC_CHARGES,
      GC.CLO_RATE_SYNC_UPLIFT_VALUE,
      GC.CLO_RATE_SYNC_UPLIFT_APPLY_TO,
    ],
  }),
  withAsyncConfigs,
  withProps((props) => {
    const { asyncConfigs } = props;

    return {
      initialValues: {
        [GC.FIELD_RATE_UNIT]: G.getConfigValueFromStore(GC.CLO_RATE_SYNC_UPLIFT_UNIT, asyncConfigs),
        [GC.FIELD_APPLY_TO]: G.getConfigValueFromStore(GC.CLO_RATE_SYNC_UPLIFT_APPLY_TO, asyncConfigs),
        [GC.FIELD_UPLIFT_VALUE]: G.getConfigValueFromStore(GC.CLO_RATE_SYNC_UPLIFT_VALUE, asyncConfigs),
        [GC.FIELD_SYNC_CHARGES]: G.getConfigValueFromStore(GC.CLO_RATE_SYNC_CHARGES, asyncConfigs) || false,
      },
    };
  }),
  withFormik({
    validationSchema,
    enableReinitialize: true,
    handleSubmit: (values, { props }) => props.submitAction(values),
    mapPropsToValues: ({ initialValues }) => G.setInitialFormikValues(
      defaultFields,
      initialValues,
    ),
  }),
  pure,
);

const EnhancedApplyOrderRateUpliftForm = enhance(ApplyOrderRateUpliftForm);

export const withApplyOrderRateUpliftForm = ({ submitActionName }) => compose(
  connect(null, { openModal, closeModal }),
  withHandlers({
    handleApplyOrderRateUpliftForm: (props) => (data) => {
      const { openModal, closeModal } = props;

      const isLegRate = R.or(props.isLegRate, R.prop('isLegRate', data));

      const title = G.ifElse(
        isLegRate,
        G.getWindowLocale('titles:apply-leg-rate-uplift', 'Apply Leg Rate Uplift'),
        G.getWindowLocale('titles:apply-order-rate-uplift', 'Apply Order Rate Uplift'),
      );

      const submitAction = (values) => {
        closeModal();
        G.callFunctionWith2Args(props[submitActionName], values, data);
      };

      const component = (
        <EnhancedApplyOrderRateUpliftForm
          closeModal={closeModal}
          submitAction={submitAction}
        />
      );

      const modal = {
        p: 15,
        component,
        options: {
          title,
          width: 'max-content',
          height: 'max-content',
        },
      };

      openModal(modal);
    },
  }),
  pure,
);

export default EnhancedApplyOrderRateUpliftForm;
